import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { ILinkTypes, IWorkItemData, IWorkItemTypeData, IFieldProjectData, IFieldEntry } from "WorkItemTracking/Scripts/OM/WorkItemInterfaces";
import { PerfScenarioManager } from "WorkItemTracking/Scripts/Utils/PerfScenarioManager";
import { getMSJSON, postMSJSON } from "Presentation/Scripts/TFS/TFS.Legacy.Ajax";
import { PerformanceEvents } from "WorkItemTracking/Scripts/CustomerIntelligence";
import { WITCommonConstants } from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { WorkItemMetadataCacheStampManager } from "WorkItemTracking/Scripts/WorkItemMetadataCacheStampManager";
import { IWorkItemDataSource } from "WorkItemTracking/Scripts/OM/DataSources/Interfaces";
import { WorkItemStoreLevelWITDataSource } from "Presentation/Scripts/TFS/MVCToDataProviderDataSources/MVCToDataProviderDataSource";
import { IRouteParams, IDataSourceResponse } from "Presentation/Scripts/TFS/MVCToDataProviderDataSources/WITDataSource";
import { WorkItemIndexDBCacheManager } from "Presentation/Scripts/TFS/MVCToDataProviderDataSources/Caching/WorkItemIndexDBCacheManager";
import WITConstants = require("Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants");
import { traceMessage } from "Presentation/Scripts/TFS/MVCToDataProviderDataSources/Trace";
import CIConstants = require("WorkItemTracking/Scripts/CustomerIntelligence");
import { WorkItemMetadataCacheInformationManager } from "WorkItemTracking/Scripts/OM/WorkItemMetadataCacheInformationManager";

export class WorkItemMvcClient implements IWorkItemDataSource {
    private _tfsContext: TfsContext;
    private _cacheStampManager: WorkItemMetadataCacheStampManager;

    constructor(tfsContext: TfsContext, cacheStampManager: WorkItemMetadataCacheStampManager) {
        this._tfsContext = tfsContext;
        this._cacheStampManager = cacheStampManager;
    }

    public beginGetWorkItemData(projectId: string, ids: number[], isDeleted?: boolean, includeInRecentActivity?: boolean, includeHistory?: boolean): Promise<IWorkItemData[]> {
        const apiName = !includeHistory ? "workitemsnohistory" : "workitems";
        const routeParams: IRouteParams = { controller: "wit", action: apiName };

        return new WorkItemStoreLevelWITDataSource().postDataAsync<IWorkItemData[]>(
            routeParams, 
            { ids: ids, isDeleted: isDeleted, includeInRecentActivity: includeInRecentActivity, includeHistory: includeHistory })
            .then((witDataSourceResponse: IDataSourceResponse<IWorkItemData[]>) => witDataSourceResponse.data);
    }

    public beginGetLinkTypes(): Promise<ILinkTypes> {
        return new Promise((resolve, reject) => {
            this._cacheStampManager.addStampToParams(WITCommonConstants.LinkTypes, null, (params) => {
                const routeParams: IRouteParams = { controller: "wit", action: "linkTypes" };

                new WorkItemStoreLevelWITDataSource().getDataFromCacheAsync<ILinkTypes>(routeParams,
                    params,
                    this._tfsContext.navigation.collection.instanceId,
                    WITConstants.WITCommonConstants.LinkTypes,
                    "_").then((response: IDataSourceResponse<ILinkTypes>) => {
                        // Below thing is conditional because as of now dataproviders do not pass any etag data back.
                        // In future when etag is used in dataproviders we will change this.
                        if (response.requestHeaders) {
                            PerfScenarioManager.addData({
                                [`${PerformanceEvents.WORKITEMTRACKING_GETLINKTYPES_REQUEST}.ETAG`]: response.requestHeaders.getResponseHeader("ETag"),
                                [`${PerformanceEvents.WORKITEMTRACKING_GETLINKTYPES_REQUEST}.DATE`]: response.requestHeaders.getResponseHeader("Date")
                            });
                        }

                        resolve(response.data);
                    }, reject);
            });
        });
    }

    public beginGetWorkItemTypeData(projectId: string, typeNames: string[]): Promise<IWorkItemTypeData[]> {
        return new Promise((resolve, reject) => {
            this._cacheStampManager.addStampToParams(WITCommonConstants.WorkItemTypes, { typeNames, projectId }, (params) => {
                PerfScenarioManager.addSplitTiming(PerformanceEvents.WORKITEMTRACKING_GETTYPES_REQUEST, true);
                const routeParams: IRouteParams = { controller: "wit", action: "workItemTypes", routeData: { project: projectId } };

                new WorkItemStoreLevelWITDataSource().getDataAsync<IWorkItemTypeData[]>(routeParams, params).then((response: IDataSourceResponse<IWorkItemTypeData[]>) => {
                    // Below thing is conditional because as of now dataproviders do not pass any etag data back.
                    // In future when etag is used in dataproviders we will change this.
                    if (response.requestHeaders) {
                        // capture etag and date , date of resource can help confirm if the data was cold loaded or warm loaded from web cache
                        PerfScenarioManager.addData({
                            [`${PerformanceEvents.WORKITEMTRACKING_GETTYPES_REQUEST}.ETAG`]: response.requestHeaders.getResponseHeader("ETag"),
                            [`${PerformanceEvents.WORKITEMTRACKING_GETTYPES_REQUEST}.DATE`]: response.requestHeaders.getResponseHeader("Date")
                        });
                    }

                    PerfScenarioManager.addSplitTiming(PerformanceEvents.WORKITEMTRACKING_GETTYPES_REQUEST, false);
                    resolve(response.data);
                }, reject);
            }, projectId);
        });
    }

    public beginGetFieldProjectData(projectId: string): Promise<IFieldProjectData> {
        return new Promise((resolve, reject) => {
            this._cacheStampManager.addStampToParams(WITCommonConstants.TeamProjects, {
                includeFieldDefinitions: true,
                namesOrIds: [projectId]
            }, params => {
                const routeParams: IRouteParams = { controller: "wit", action: "teamProjects", routeData: { project: projectId } };

                new WorkItemStoreLevelWITDataSource().getDataFromCacheAsync<IFieldProjectData>(
                    routeParams, params, projectId, WITConstants.WITCommonConstants.TeamProjects, projectId).then((witDataSourceResponse: IDataSourceResponse<IFieldProjectData>) => {
                        resolve(witDataSourceResponse.data);
                    }, (error) => {
                        reject(error);
                    });
            });
        });
    }

    public beginGetFields(): Promise<IFieldEntry[]> {
        return new Promise((resolve, reject) => {
            this._cacheStampManager.addStampToParams(WITCommonConstants.Fields, null, (params) => {
                getMSJSON(this._getApiLocation(undefined, "fields"), params, (fields: IFieldEntry[]) => {
                    resolve(fields);
                }, (error) => {
                    reject(error);
                });
            });
        });
    }

    private _getApiLocation(projectId: string | undefined, action: string, params?: Object): string {
        // Do not include team
        return this._tfsContext.getActionUrl(action || "", "wit", $.extend({ project: projectId, team: "", area: "api" }, params));
    }
}
